// components/island/states/AnrufContent.tsx
// Anrufkarte in der Pille. Reine Darstellung — was angezeigt wird, entscheidet
// lib/telefonie/anrufKarte.ts (dort auch die Tests; Komponententests sind in
// diesem Projekt nicht möglich, vitest läuft mit environment 'node').
import type { KeyboardEvent } from 'react';
import { Buildings, CaretRight, Phone, PhoneIncoming, PhoneOutgoing, PhoneX, User, X } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../../lib/cn';
import { supabase } from '../../../lib/supabase/client';
import { anrufZielPfad } from '../../../lib/telefonie/anrufZiel';
import { useIslandState, useIslandActions } from '../IslandProvider';
import { OHNE_NAMEN } from '../../../lib/telefonie/anrufKarte';
import type { AnrufKontext } from '../../../lib/telefonie/anrufKontext';
// Gleiche Status-Label-Quelle wie die Ticket-Liste/-Kanban (keine eigene
// Übersetzung pflegen) — statt des rohen DB-Status ("warten_auf_dienstleister").
import { statusPill } from '../../ui/statusStyles';
import { FARBE, getoent } from '../pille-stil';

/** Gemeinsamer Fokusring für die neuen Zeilen im Zusammenhang-Block — dieselbe
 *  Klasse wie in DynamicIsland.tsx, damit Tastaturnutzer sehen, wo sie stehen. */
const FOKUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e2c08d]/60';

/** Stoppt die Ereignis-Weitergabe nur für Enter/Leertaste — das schluckt den
 *  Öffnen-Klick der Pillen-Shell (die auf Enter/Space reagiert), lässt aber
 *  alle anderen Tasten (Escape, ⌘K) zu den globalen window-Listenern in
 *  DynamicIsland.tsx durch. */
function stoppeAktivierungstasten(e: KeyboardEvent) {
  if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
}

/** Karte kürzt lange Titel selbst (truncate), Tickets kommen aber teils mit
 *  sehr langen Titeln — ein hartes Limit hält die Zeile einzeilig lesbar,
 *  auch wenn CSS-truncate aus irgendeinem Grund nicht greift. */
const TITEL_MAX = 40;

function titelGekuerzt(titel: string | null): string {
  const t = (titel || '').trim();
  return t.length > TITEL_MAX ? `${t.slice(0, TITEL_MAX - 1)}…` : t;
}

export function AnrufContent({ kontext }: { kontext: AnrufKontext | null }) {
  const { anruf } = useIslandState();
  const { schliesseAnruf, notify } = useIslandActions();
  const navigate = useNavigate();

  function oeffneKontakt() {
    if (!anruf?.ziel) return;
    const pfad = anrufZielPfad(anruf.ziel);
    if (!pfad) return;
    schliesseAnruf();
    navigate(pfad);
  }

  function oeffneTicket(id: string) {
    schliesseAnruf();
    navigate(`/admin/tickets/${id}`);
  }

  /** Auswahl festschreiben: der gewählte Kandidat wird zum Treffer des Anrufs.
   *  Damit greift auch der Datenbank-Trigger und schreibt den Timeline-Eintrag
   *  beim richtigen Kontakt. Kein lokales Nachziehen nötig: die Änderung kommt
   *  über Realtime zurück. */
  async function waehle(eintrag: { typ: string; id: string; name: string; objekt_id: string | null }) {
    if (!anruf) return;
    const update: {
      treffer_typ: string; treffer_id: string; treffer_name?: string; objekt_id: string | null; kandidaten: null;
    } = {
      treffer_typ: eintrag.typ,
      treffer_id: eintrag.id,
      // Die Kandidatenliste bringt das Objekt schon mit — sonst bliebe
      // anrufe.objekt_id nach einer manuellen Auswahl dauerhaft leer und die
      // Objektzeile im Zusammenhang fehlte, obwohl der Wert bekannt war.
      objekt_id: eintrag.objekt_id,
      kandidaten: null,
    };
    // Platzhalter-Name nicht als treffer_name schreiben — sonst landet
    // "Ohne Namen" als Betreff in der Kontakt-Timeline. Bleibt das Feld leer,
    // nimmt der Datenbank-Trigger stattdessen die Nummer.
    if (eintrag.name !== OHNE_NAMEN) update.treffer_name = eintrag.name;
    const { error } = await supabase.from('anrufe').update(update).eq('id', anruf.id);
    if (error) {
      console.error('Anrufer zuordnen:', error.message);
      notify({ title: 'Anrufer konnte nicht zugeordnet werden', tone: 'error' });
    }
  }

  if (!anruf) return null;

  // Iconwahl an den Daten (richtung/verpasst), nicht am Anzeigetext — sonst
  // zeigt z.B. ein beendeter ausgehender Anruf fälschlich das Eingehend-Icon,
  // und jede Textänderung in anrufKarte.ts bricht die Iconwahl stumm.
  const Icon = anruf.verpasst
    ? PhoneX
    : anruf.statusText === 'Im Gespräch'
      ? Phone
      : anruf.richtung === 'ausgehend'
        ? PhoneOutgoing
        : PhoneIncoming;

  const farbe = anruf.verpasst ? FARBE.rotHell : FARBE.gruen;
  const initialen = anruf.ziel && !anruf.unbekannt ? initialenAus(anruf.titel) : '';
  // Nummer nicht doppelt zeigen, wenn sie schon der Titel ist (unbekannt/Auswahl)
  const unterzeile = [anruf.rolle, anruf.rolle && anruf.nummer ? anruf.nummer : null].filter(Boolean).join(' · ')
    || (anruf.nummer && anruf.nummer !== anruf.titel ? anruf.nummer : '');

  return (
    <div role="status" className="flex flex-col" style={{ gap: 10, padding: '14px 14px 14px 16px' }}>
      <div className="flex items-center" style={{ gap: 14 }}>
        <span className="relative flex shrink-0 items-center justify-center rounded-full"
          style={{ width: 52, height: 52, background: anruf.verpasst ? 'linear-gradient(160deg,#ff6961,#c8423b)' : 'linear-gradient(160deg,#5fd38a,#2a8f5c)' }}>
          {!anruf.verpasst && !anruf.erledigt && (
            <span aria-hidden className="pille-puls absolute rounded-full" style={{ inset: -4, boxShadow: `0 0 0 2px ${getoent(FARBE.gruen, 0.55)}` }} />
          )}
          {initialen
            ? <span style={{ fontSize: 17, fontWeight: 800 }}>{initialen}</span>
            : <User size={22} weight="fill" />}
        </span>

        <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
          <span className="flex items-center uppercase" style={{ gap: 5, fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: farbe }}>
            <Icon size={13} weight="fill" />
            <span className="truncate">{anruf.statusText}</span>
          </span>
          {anruf.ziel ? (
            <button
              type="button"
              // stopPropagation: ein geschlossener 'anruf'-Zustand legt einen Öffnen-Klick aufs
              // Wurzel-Element, sonst reißt der Klick zusätzlich das Deck auf.
              // Nur für Enter/Space, sonst würden Escape und ⌘K nie die globalen Listener erreichen.
              onClick={(e) => { e.stopPropagation(); oeffneKontakt(); }}
              onKeyDown={stoppeAktivierungstasten}
              className={cn('block max-w-full cursor-pointer truncate text-left', FOKUS_RING)}
              style={{ fontSize: 17, fontWeight: 800, padding: 0, border: 'none', background: 'transparent', color: '#fff' }}
            >
              {anruf.titel}
            </button>
          ) : (
            <span className="block truncate" style={{ fontSize: 17, fontWeight: 800 }}>{anruf.titel}</span>
          )}
          {unterzeile && <span className="truncate" style={{ fontSize: 12, color: 'rgba(255,255,255,0.62)' }}>{unterzeile}</span>}
        </span>

        <button
          type="button"
          // stopPropagation: die Shell öffnet im 'anruf'-Modus per Klick/Enter das Deck.
          onClick={(e) => { e.stopPropagation(); schliesseAnruf(); }}
          onKeyDown={stoppeAktivierungstasten}
          aria-label="Anrufkarte schließen"
          className={cn('tap-44 flex shrink-0 cursor-pointer items-center justify-center self-start', FOKUS_RING)}
          style={{ border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.8)', margin: '-7px -7px 0 0' }}
        >
          <span className="flex items-center justify-center rounded-full" style={{ width: 30, height: 30, background: 'rgba(255,255,255,0.1)' }}>
            <X size={13} weight="bold" />
          </span>
        </button>
      </div>

      {(kontext?.hatInhalt || kontext?.fehler) && (
        <div className="flex min-h-0 flex-col" style={{ gap: 4, paddingTop: 8, borderTop: '0.5px solid rgba(255,255,255,0.12)' }}>
          {kontext.objektZeile && (
            <div className="flex items-center" style={{ gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.62)', minHeight: 18 }}>
              <Buildings size={13} weight="fill" color={FARBE.gold} className="shrink-0" />
              <span className="truncate">{kontext.objektZeile}</span>
            </div>
          )}
          {kontext.tickets.length > 0 && (
            // max-h + overflow-y-auto: die Höhenschätzung in island-size.ts ist eine Annäherung —
            // geht sie daneben, scrollt die Liste statt die unterste Zeile abzuschneiden.
            <div className="flex flex-col overflow-y-auto" style={{ gap: 4, maxHeight: 144 }}>
              {kontext.tickets.map((t) => {
                const dringend = t.prioritaet === 'dringend';
                const chip = dringend ? '#ffb35c' : '#7aa7ff';
                return (
                  <button
                    key={t.id}
                    type="button"
                    // stopPropagation: siehe Schließen-Knopf — sonst öffnet der Klick zusätzlich das Deck.
                    onClick={(e) => { e.stopPropagation(); oeffneTicket(t.id); }}
                    onKeyDown={stoppeAktivierungstasten}
                    className={cn('flex w-full shrink-0 cursor-pointer items-center text-left transition-colors hover:bg-white/[0.11]', FOKUS_RING)}
                    style={{ height: 44, gap: 10, padding: '0 10px', borderRadius: 13, border: 'none', background: 'rgba(255,255,255,0.07)', color: '#fff' }}
                  >
                    <span className="shrink-0 tabular-nums" style={{ height: 22, padding: '0 8px', borderRadius: 11, fontSize: 11, fontWeight: 800, lineHeight: '22px', color: chip, background: getoent(chip, 0.18) }}>
                      {t.ticket_nummer}
                    </span>
                    <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13, fontWeight: 600 }}>{titelGekuerzt(t.titel)}</span>
                    <span className="shrink-0 uppercase" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', color: 'rgba(255,255,255,0.45)' }}>
                      {statusPill(t.status).label}
                    </span>
                    <CaretRight size={13} weight="bold" className="shrink-0" color="rgba(255,255,255,0.45)" />
                  </button>
                );
              })}
            </div>
          )}
          {kontext.weitereTickets > 0 && (
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', minHeight: 18 }}>
              + {kontext.weitereTickets} {kontext.weitereTickets === 1 ? 'weiterer Vorgang' : 'weitere Vorgänge'}
            </span>
          )}
          {kontext.fehler && (
            <p className="m-0" style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', minHeight: 18 }}>
              Zusammenhang konnte nicht vollständig geladen werden.
            </p>
          )}
        </div>
      )}

      {anruf.auswahl && (
        <div className="flex min-h-0 flex-col" style={{ gap: 6 }}>
          <span className="uppercase" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', color: 'rgba(255,255,255,0.45)' }}>
            Wer ruft an?
          </span>
          <div className="flex flex-col overflow-y-auto" style={{ gap: 4, maxHeight: 96 }}>
            {anruf.auswahl.map((k) => (
              <button
                key={`${k.typ}:${k.id}`}
                type="button"
                // stopPropagation: siehe Schließen-Knopf — sonst öffnet der Klick zusätzlich das Deck.
                onClick={(e) => { e.stopPropagation(); void waehle(k); }}
                onKeyDown={stoppeAktivierungstasten}
                className={cn('flex w-full shrink-0 cursor-pointer items-center text-left transition-colors hover:bg-white/[0.12]', FOKUS_RING)}
                style={{ height: 44, gap: 10, padding: '0 10px', borderRadius: 14, border: 'none', background: 'rgba(255,255,255,0.08)', color: '#fff' }}
              >
                <span className="flex shrink-0 items-center justify-center rounded-full" style={{ width: 28, height: 28, fontSize: 11, fontWeight: 800, background: 'rgba(255,255,255,0.14)' }}>
                  {initialenAus(k.name)}
                </span>
                <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13, fontWeight: 700 }}>{k.name}</span>
                <span className="shrink-0" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{k.rolle}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {anruf.unbekannt && (
        <p className="m-0" style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
          Diese Nummer ist keinem Kontakt zugeordnet.
        </p>
      )}
    </div>
  );
}

/** „Nadine Kroplewski“ → „NK“ */
function initialenAus(name: string | null | undefined): string {
  const w = (name ?? '').replace(/[^\p{L}\s-]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (w.length === 0) return '';
  return ((w[0]![0] ?? '') + (w.length > 1 ? w[w.length - 1]![0] ?? '' : '')).toUpperCase();
}
